import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CoreSpinLoader from './CoreSpinLoader';

/**
 * Global helper to trigger the route loader when clicking in-page tabs or jump links
 */
export const navigateWithLoader = (targetId) => {
  window.dispatchEvent(new CustomEvent('sipl:navigate-to', { detail: { targetId } }));
};

/**
 * RouteChangeLoader
 *
 * On every route change or tab/hash link navigation:
 *   1. Shows the CoreSpinLoader overlay immediately (covers the screen)
 *   2. Temporarily disables smooth scrolling (scrollBehavior = 'auto')
 *   3. Scrolls window to top (0,0) or to target anchor element invisibly behind the loader
 *   4. Fades out the loader after hold duration — user only sees the clean loading state,
 *      no visible scrolling or UI jumping!
 */
const RouteChangeLoader = () => {
  const { pathname, hash, key } = useLocation();
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef(null);
  const isFirst = useRef(true);

  // Route and Hash changes (links, tabs, dropdowns)
  useEffect(() => {
    // Skip loader on very first initial page load (IntroAnimation handles that)
    if (isFirst.current) {
      isFirst.current = false;
      if (!hash) {
        window.scrollTo(0, 0);
      } else {
        const el = document.getElementById(hash.replace('#', ''));
        if (el) {
          const offset = el.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top: offset, left: 0, behavior: 'auto' });
        }
      }
      return;
    }

    // Immediately show loader to mask scroll changes
    clearTimeout(hideTimerRef.current);
    setVisible(true);

    const rootHtml = document.documentElement;
    const prevScrollBehavior = rootHtml.style.scrollBehavior;
    rootHtml.style.scrollBehavior = 'auto';

    const scrollTask = setTimeout(() => {
      if (hash) {
        const targetId = hash.replace('#', '');
        const el = document.getElementById(targetId);
        if (el) {
          const offset = el.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top: offset, left: 0, behavior: 'auto' });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
      rootHtml.style.scrollBehavior = prevScrollBehavior;
    }, 50);

    hideTimerRef.current = setTimeout(() => {
      setVisible(false);
    }, 450);

    return () => {
      clearTimeout(scrollTask);
      clearTimeout(hideTimerRef.current);
      rootHtml.style.scrollBehavior = prevScrollBehavior;
    };
  }, [pathname, hash, key]);

  // In-page navigation events (e.g. solution/service index tabs)
  useEffect(() => {
    const handleCustomNav = (e) => {
      const targetId = e.detail?.targetId;
      clearTimeout(hideTimerRef.current);
      setVisible(true);

      const rootHtml = document.documentElement;
      const prevScrollBehavior = rootHtml.style.scrollBehavior;
      rootHtml.style.scrollBehavior = 'auto';

      setTimeout(() => {
        if (targetId) {
          const el = document.getElementById(targetId);
          if (el) {
            const offset = el.getBoundingClientRect().top + window.pageYOffset - 80;
            window.scrollTo({ top: offset, left: 0, behavior: 'auto' });
          }
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
        rootHtml.style.scrollBehavior = prevScrollBehavior;
      }, 50);

      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
      }, 450);
    };

    window.addEventListener('sipl:navigate-to', handleCustomNav);
    return () => window.removeEventListener('sipl:navigate-to', handleCustomNav);
  }, []);

  return <CoreSpinLoader visible={visible} />;
};

export default RouteChangeLoader;
